import { useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Typography
} from '@mui/material';
import { adminApi } from '@/services';
import { authClient } from '@/lib/auth-client';
import StorageIcon from '@mui/icons-material/Storage';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RestoreIcon from '@mui/icons-material/Restore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

type RestoreState =
	| { phase: 'idle' }
	| { phase: 'confirming' }
	| { phase: 'loading' }
	| { phase: 'success' }
	| { phase: 'error'; message: string };

export const DatabaseBackupCard = () => {
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [isDownloading, setIsDownloading] = useState(false);
	const [downloadError, setDownloadError] = useState<string | null>(null);

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [restoreState, setRestoreState] = useState<RestoreState>({ phase: 'idle' });

	const handleDownload = async () => {
		setIsDownloading(true);
		setDownloadError(null);
		try {
			await adminApi.downloadBackup();
		} catch (err) {
			setDownloadError(
				err instanceof Error ? err.message : 'Không thể tải file backup. Vui lòng thử lại.'
			);
		} finally {
			setIsDownloading(false);
		}
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0] ?? null;
		setSelectedFile(file);
		setRestoreState({ phase: 'idle' });
		event.target.value = '';
	};

	const handleOpenFileDialog = () => {
		fileInputRef.current?.click();
	};

	const handleConfirmRestore = async () => {
		if (!selectedFile) return;
		setRestoreState({ phase: 'loading' });

		try {
			await adminApi.restoreBackup(selectedFile);
			setSelectedFile(null);
			setRestoreState({ phase: 'success' });
		} catch (err) {
			setRestoreState({
				phase: 'error',
				message: isAxiosError<{ message?: string }>(err)
					? err.response?.data?.message ?? err.message
					: 'Khôi phục thất bại. Vui lòng thử lại.',
			});
		}
	};

	const handleSuccessOk = async () => {
		try {
			await authClient.signOut();
		} catch {
			// session already gone after restore — ignore
		}
		window.location.href = '/login';
	};

	const handleErrorOk = () => {
		setRestoreState({ phase: 'idle' });
	};

	const isRestoring = restoreState.phase === 'loading';

	return (
		<Card>
			<CardContent>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
					<StorageIcon color="action" />
					<Typography variant="h6">
						Sao lưu &amp; Khôi phục CSDL
					</Typography>
				</Box>

				{/* ── Backup section ── */}
				<Box sx={{ mb: 3 }}>
					<Typography variant="subtitle2" color="text.secondary" gutterBottom>
						Sao lưu
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
						Tải file backup (.dump) chứa toàn bộ dữ liệu.
					</Typography>
					<Button
						variant="contained"
						color="primary"
						startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
						onClick={handleDownload}
						disabled={isDownloading || isRestoring}
						fullWidth
						sx={{ minHeight: 40 }}
					>
						{isDownloading ? 'Đang tải...' : 'Tải backup'}
					</Button>
					{downloadError && (
						<Alert severity="error" sx={{ mt: 1.5 }}>
							{downloadError}
						</Alert>
					)}
				</Box>

				<Divider sx={{ mb: 3 }} />

				{/* ── Restore section ── */}
				<Box>
					<Typography variant="subtitle2" color="text.secondary" gutterBottom>
						Khôi phục
					</Typography>

					<Chip
						icon={<WarningAmberIcon />}
						label="Thao tác này sẽ ghi đè toàn bộ dữ liệu hiện tại"
						color="warning"
						size="small"
						sx={{ mb: 1.5, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
					/>

					{/* Hidden file input */}
					<input
						ref={fileInputRef}
						type="file"
						accept=".dump"
						style={{ display: 'none' }}
						onChange={handleFileChange}
					/>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
						{selectedFile && (
							<Chip
								icon={<InsertDriveFileIcon />}
								label={selectedFile.name}
								variant="outlined"
								size="small"
								onDelete={() => setSelectedFile(null)}
								sx={{ alignSelf: 'flex-start' }}
							/>
						)}

						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: { xs: '1fr', sm: selectedFile ? '1fr 1fr' : '1fr' },
								gap: 1.5,
							}}
						>
							<Button
								variant="outlined"
								color="secondary"
								startIcon={<UploadFileIcon />}
								onClick={handleOpenFileDialog}
								disabled={isRestoring}
								fullWidth
								sx={{ minHeight: 40 }}
							>
								{selectedFile ? 'Chọn file khác' : 'Chọn file backup (.dump)'}
							</Button>

							{selectedFile && (
								<Button
									variant="contained"
									color="error"
									startIcon={<RestoreIcon />}
									onClick={() => setRestoreState({ phase: 'confirming' })}
									disabled={isRestoring}
									fullWidth
									sx={{ minHeight: 40 }}
								>
									Khôi phục
								</Button>
							)}
						</Box>
					</Box>
				</Box>
			</CardContent>

			{/* ── Confirm dialog ── */}
			<Dialog open={restoreState.phase === 'confirming'} onClose={() => setRestoreState({ phase: 'idle' })}>
				<DialogTitle>Xác nhận khôi phục</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Toàn bộ dữ liệu hiện tại sẽ bị xóa và thay thế bằng dữ liệu từ file backup. Tài khoản admin hiện tại sẽ được giữ nguyên. Bạn có chắc chắn không?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRestoreState({ phase: 'idle' })}>Hủy</Button>
					<Button onClick={handleConfirmRestore} color="error" variant="contained">
						Khôi phục
					</Button>
				</DialogActions>
			</Dialog>

			{/* ── Loading dialog (blocks all interaction + reload) ── */}
			<Dialog
				open={restoreState.phase === 'loading'}
				disableEscapeKeyDown
				onClose={() => {/* blocked */}}
				PaperProps={{ sx: { minWidth: 320, textAlign: 'center' } }}
			>
				<DialogContent sx={{ py: 4 }}>
					<CircularProgress size={56} sx={{ mb: 2 }} />
					<Typography variant="h6" gutterBottom>
						Đang khôi phục cơ sở dữ liệu...
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Vui lòng không đóng tab hoặc tải lại trang.
					</Typography>
				</DialogContent>
			</Dialog>

			{/* ── Success dialog — only OK to login ── */}
			<Dialog
				open={restoreState.phase === 'success'}
				disableEscapeKeyDown
				onClose={() => {/* blocked */}}
				PaperProps={{ sx: { minWidth: 320, textAlign: 'center' } }}
			>
				<DialogContent sx={{ py: 4 }}>
					<CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
					<Typography variant="h6" gutterBottom>
						Khôi phục thành công!
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại để tiếp tục.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
					<Button variant="contained" onClick={handleSuccessOk} autoFocus>
						OK — Đăng nhập lại
					</Button>
				</DialogActions>
			</Dialog>

			{/* ── Error dialog ── */}
			<Dialog
				open={restoreState.phase === 'error'}
				onClose={handleErrorOk}
				PaperProps={{ sx: { minWidth: 320, textAlign: 'center' } }}
			>
				<DialogContent sx={{ py: 4 }}>
					<ErrorOutlineIcon color="error" sx={{ fontSize: 56, mb: 2 }} />
					<Typography variant="h6" gutterBottom>
						Khôi phục thất bại
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{restoreState.phase === 'error' ? restoreState.message : ''}
					</Typography>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
					<Button variant="outlined" onClick={handleErrorOk}>
						Đóng
					</Button>
				</DialogActions>
			</Dialog>
		</Card>
	);
};
